import { API, graphqlOperation } from "aws-amplify";
import * as mutations from "../graphql/mutations/users";
import * as queries from "../graphql/queries/users";
import mime from "mime";

class UsersAPI implements IUsersAPI {
  async adminListUsers() {
    try {
      const response: any = await API.graphql(graphqlOperation(queries.adminListUsers));
      return response.data.adminListUsers as IUser[];
    } catch (e) {
      throw e;
    }
  }

  async adminGetUser(userId: string) {
    try {
      const response: any = await API.graphql(graphqlOperation(queries.adminGetUser, { userId }));
      return response.data.adminGetUser as IUser;
    } catch (e) {
      throw e;
    }
  }

  async adminUpdateUser(user: IUser) {
    try {
      const response: any = await API.graphql(
        graphqlOperation(mutations.adminUpdateUser, { input: user })
      );
      return response.data.adminUpdateUser as IUser;
    } catch (e) {
      throw e;
    }
  }

  async adminCreateUser(user: IUser) {
    try {
      const response: any = await API.graphql(
        graphqlOperation(mutations.adminCreateUser, { input: user })
      );
      return response.data.adminCreateUser as IUser;
    } catch (e) {
      throw e;
    }
  }

  async adminDeleteUser(userId: string) {
    try {
      const response: any = await API.graphql(
        graphqlOperation(mutations.adminDeleteUser, { userId })
      );
      return response.data.adminDeleteUser as IUser;
    } catch (e) {
      throw e;
    }
  }

  async userListUsers() {
    try {
      const response: any = await API.graphql(graphqlOperation(queries.userListUsers));
      return response.data.userListUsers as IUser[];
    } catch (e) {
      throw e;
    }
  }

  async userGetUser(userId: string) {
    try {
      const response: any = await API.graphql(graphqlOperation(queries.userGetUser, { userId }));
      return response.data.userGetUser as IUser;
    } catch (e) {
      throw e;
    }
  }

  async userUpdateUser(user: IUser) {
    try {
      const response: any = await API.graphql(
        graphqlOperation(mutations.userUpdateUser, { input: user })
      );
      return response.data.userUpdateUser as IUser;
    } catch (e) {
      throw e;
    }
  }

  async userGetUploadUrl(input: IUserGetUploadUrlParams) {
    try {
      const response: any = await API.graphql(
        graphqlOperation(queries.userGetUploadUrl, { input })
      );
      const preSignedUrl = response.data.userGetUploadUrl as IPreSignedUrl;

      return preSignedUrl;
    } catch (e) {
      throw e;
    }
  }

  async userUploadPrivateFile(file: File, path: string, filename?: string) {
    try {
      const contentType = mime.getType(file.name);
      if (contentType) {
        const preSignedUrl = await this.userGetUploadUrl({
          handler: "users/private",
          filename: filename || file.name,
          contentType,
          path
        });

        await fetch(preSignedUrl.url, {
          method: "PUT",
          body: file
        });

        return preSignedUrl;
      }
      throw new Error("WRONT_CONTENT_TYPE");
    } catch (e) {
      throw e;
    }
  }

  async userUploadProtectedFile(file: File, path: string, filename?: string) {
    try {
      const contentType = mime.getType(file.name);
      if (contentType) {
        const preSignedUrl = await this.userGetUploadUrl({
          handler: "users/protected",
          filename: filename || file.name,
          contentType,
          path
        });

        await fetch(preSignedUrl.url, {
          method: "PUT",
          body: file
        });

        return preSignedUrl;
      }
      throw new Error("WRONT_CONTENT_TYPE");
    } catch (e) {
      throw e;
    }
  }

  async userUploadPublicFile(file: File, path: string, filename?: string) {
    try {
      const contentType = mime.getType(file.name);
      if (contentType) {
        const preSignedUrl = await this.userGetUploadUrl({
          handler: "users/public",
          filename: filename || file.name,
          contentType,
          path
        });

        await fetch(preSignedUrl.url, {
          method: "PUT",
          body: file
        });

        return preSignedUrl;
      }
      throw new Error("WRONT_CONTENT_TYPE");
    } catch (e) {
      throw e;
    }
  }

  userValidatePassword(password: string) {
    let errors: string[] = [];
    if (password.length < 8) errors.push("Password must have at least 8 characters");
    if (!password.match(/\d/)) errors.push("Password must have numbers");
    if (!password.match(/(?=.*[a-z])/)) errors.push("Password must have lowercase letters");
    if (!password.match(/(?=.*[A-Z])/)) errors.push("Password must have uppercase letters");

    return errors;
  }
}

export default new UsersAPI();
